import { useCallback, useMemo, useState } from "react";
import type {
  Clarity,
  Condition,
  Currency,
  CutGrade,
  Metal,
  MetalColor,
  OwnershipStatus,
  Piece,
  PieceKind,
  PieceWrite,
  StoneShape,
  StoneType,
  YearPrecision,
} from "../../api/types";
import { ValidationError } from "../../api/errors";
import { tr } from "../../i18n/strings";

/** One stone line as edited. `key` is local: stones are replaced as a list on save. */
export interface StoneDraft {
  key: string;
  stone_type: StoneType | "";
  shape: StoneShape | "";
  count: number | null;
  carat: number | null;
  color: string;
  clarity: Clarity | "";
  cut: CutGrade | "";
  certificate_ref: string;
  notes: string;
  price: number | null;
  currency: Currency;
}

export interface FormState {
  title: string;
  inventory_code: string;
  kind: PieceKind | "";
  metal: Metal | "";
  metal_color: MetalColor | "";
  purity_permille: number | null;
  gross_weight_g: number | null;
  net_weight_g: number | null;
  hallmarks: string;
  maker: string;
  size_label: string;
  year_precision: YearPrecision;
  year_start: number | null;
  year_end: number | null;
  ownership_status: OwnershipStatus | "";
  making_charge_percent: number | null;
  condition: Condition | "";
  condition_notes: string;
  provenance_text: string;
  authenticity_notes: string;
  notes: string;
  stones: StoneDraft[];
}

export const blankForm: FormState = {
  title: "",
  inventory_code: "",
  kind: "",
  metal: "",
  metal_color: "",
  purity_permille: null,
  gross_weight_g: null,
  net_weight_g: null,
  hallmarks: "",
  maker: "",
  size_label: "",
  year_precision: "unknown",
  year_start: null,
  year_end: null,
  ownership_status: "",
  making_charge_percent: null,
  condition: "",
  condition_notes: "",
  provenance_text: "",
  authenticity_notes: "",
  notes: "",
  stones: [],
};

let nextKey = 0;
const newKey = () => `stone-${++nextKey}`;

export function blankStone(): StoneDraft {
  return {
    key: newKey(),
    stone_type: "",
    shape: "",
    count: 1,
    carat: null,
    color: "",
    clarity: "",
    cut: "",
    certificate_ref: "",
    notes: "",
    price: null,
    currency: "IRT",
  };
}

export function formFromPiece(piece: Piece): FormState {
  return {
    title: piece.title,
    inventory_code: piece.inventory_code,
    kind: piece.kind,
    metal: piece.metal,
    metal_color: piece.metal_color,
    purity_permille: piece.purity_permille,
    gross_weight_g: piece.gross_weight_g,
    net_weight_g: piece.net_weight_g,
    hallmarks: piece.hallmarks,
    maker: piece.maker,
    size_label: piece.size_label,
    year_precision: piece.year_precision,
    year_start: piece.year_start,
    year_end: piece.year_end,
    ownership_status: piece.ownership_status,
    making_charge_percent: piece.making_charge_percent,
    condition: piece.condition,
    condition_notes: piece.condition_notes,
    provenance_text: piece.provenance_text,
    authenticity_notes: piece.authenticity_notes,
    notes: piece.notes,
    stones: piece.stones.map((stone) => ({
      key: stone.id ?? newKey(),
      stone_type: stone.stone_type,
      shape: stone.shape,
      count: stone.count,
      carat: stone.carat,
      color: stone.color,
      clarity: stone.clarity,
      cut: stone.cut,
      certificate_ref: stone.certificate_ref,
      notes: stone.notes,
      price: stone.price,
      currency: stone.currency,
    })),
  };
}

/** Top-level fields, plus `stones.<index>.<field>` for a stone line. */
export type FormErrors = Partial<Record<string, string>>;

export const stoneError = (errors: FormErrors, index: number, field: keyof StoneDraft) =>
  errors[`stones.${index}.${field}`];

/**
 * Mirrors the server's validators so the user sees the problem before the
 * request. The server still enforces all of it — `applyServerErrors` puts a
 * 400 back onto the same fields.
 */
export function validateForm(state: FormState): FormErrors {
  const t = tr();
  const errors: FormErrors = {};

  if (!state.title.trim()) errors.title = t.form.requiredTitle;
  if (!state.kind) errors.kind = t.form.requiredKind;

  const purity = state.purity_permille;
  if (purity !== null) {
    if (!Number.isInteger(purity)) errors.purity_permille = t.form.purityInteger;
    else if (purity < 1 || purity > 1000) errors.purity_permille = t.form.purityRange;
  }

  const { gross_weight_g: gross, net_weight_g: net } = state;
  if (gross !== null && gross <= 0) errors.gross_weight_g = t.form.weightPositive;
  if (net !== null && net <= 0) errors.net_weight_g = t.form.weightPositive;
  else if (net !== null && gross !== null && net > gross)
    errors.net_weight_g = t.form.netAboveGross;

  if (state.making_charge_percent !== null && state.making_charge_percent < 0) {
    errors.making_charge_percent = t.form.makingNegative;
  }

  if (state.year_precision === "unknown") {
    // Nothing to check: the payload drops both bounds.
  } else if (state.year_start === null) {
    errors.year_start = t.form.yearStartRequired;
  } else if (state.year_precision === "range") {
    if (state.year_end === null) errors.year_end = t.form.yearEndRequired;
    else if (state.year_end < state.year_start) errors.year_end = t.form.yearOrder;
  }

  state.stones.forEach((stone, index) => {
    if (!stone.stone_type) errors[`stones.${index}.stone_type`] = t.form.requiredStoneType;
    if (stone.count !== null && (!Number.isInteger(stone.count) || stone.count < 1)) {
      errors[`stones.${index}.count`] = t.form.countMin;
    }
    if (stone.carat !== null && stone.carat <= 0) {
      errors[`stones.${index}.carat`] = t.form.caratPositive;
    }
    if (stone.price !== null && stone.price < 0) {
      errors[`stones.${index}.price`] = t.errors.amountNegative;
    }
  });

  return errors;
}

function finiteOrNull(value: number | null): number | null {
  return value !== null && Number.isFinite(value) ? value : null;
}

/** Exact and circa are a single year; the server stores it as a degenerate range. */
export function toPayload(state: FormState): PieceWrite {
  const unknown = state.year_precision === "unknown";
  const range = state.year_precision === "range";

  return {
    title: state.title.trim(),
    inventory_code: state.inventory_code.trim(),
    kind: state.kind as PieceKind,
    metal: state.metal,
    metal_color: state.metal_color,
    purity_permille: finiteOrNull(state.purity_permille),
    gross_weight_g: finiteOrNull(state.gross_weight_g),
    net_weight_g: finiteOrNull(state.net_weight_g),
    hallmarks: state.hallmarks.trim(),
    maker: state.maker.trim(),
    size_label: state.size_label.trim(),
    year_precision: state.year_precision,
    year_start: unknown ? null : state.year_start,
    year_end: unknown ? null : range ? state.year_end : state.year_start,
    ownership_status: state.ownership_status,
    making_charge_percent: finiteOrNull(state.making_charge_percent),
    condition: state.condition,
    condition_notes: state.condition_notes,
    provenance_text: state.provenance_text,
    authenticity_notes: state.authenticity_notes,
    notes: state.notes,
    stones: state.stones.map((stone) => ({
      stone_type: stone.stone_type as StoneType,
      shape: stone.shape,
      count: stone.count ?? 1,
      carat: finiteOrNull(stone.carat),
      color: stone.color.trim(),
      clarity: stone.clarity,
      cut: stone.cut,
      certificate_ref: stone.certificate_ref.trim(),
      notes: stone.notes.trim(),
      price: finiteOrNull(stone.price),
      currency: stone.currency,
    })),
  };
}

export function usePieceForm(initial: FormState) {
  const [state, setState] = useState<FormState>(initial);
  const [errors, setErrors] = useState<FormErrors>({});
  const [baseline, setBaseline] = useState<FormState>(initial);

  const set = useCallback(<K extends keyof FormState>(key: K, value: FormState[K]) => {
    setState((current) => ({ ...current, [key]: value }));
    setErrors((current) => (current[key] ? { ...current, [key]: undefined } : current));
  }, []);

  const patch = useCallback((changes: Partial<FormState>) => {
    setState((current) => ({ ...current, ...changes }));
  }, []);

  const setStone = useCallback((index: number, changes: Partial<StoneDraft>) => {
    setState((current) => ({
      ...current,
      stones: current.stones.map((stone, at) => (at === index ? { ...stone, ...changes } : stone)),
    }));
    setErrors((current) => {
      const next = { ...current };
      for (const field of Object.keys(changes)) delete next[`stones.${index}.${field}`];
      return next;
    });
  }, []);

  const addStone = useCallback(() => {
    setState((current) => ({ ...current, stones: [...current.stones, blankStone()] }));
  }, []);

  /** Row errors are keyed by index, so removing a line invalidates all of them. */
  const removeStone = useCallback((index: number) => {
    setState((current) => ({
      ...current,
      stones: current.stones.filter((_, at) => at !== index),
    }));
    setErrors((current) =>
      Object.fromEntries(Object.entries(current).filter(([key]) => !key.startsWith("stones."))),
    );
  }, []);

  const reset = useCallback((next: FormState) => {
    setState(next);
    setBaseline(next);
    setErrors({});
  }, []);

  const dirty = useMemo(
    () => JSON.stringify(state) !== JSON.stringify(baseline),
    [state, baseline],
  );

  const check = useCallback(() => {
    const found = validateForm(state);
    setErrors(found);
    return Object.keys(found).length === 0;
  }, [state]);

  /** Maps a DRF 400 back onto the inputs that produced it. */
  const applyServerErrors = useCallback((error: unknown) => {
    if (!(error instanceof ValidationError)) return;
    const mapped: FormErrors = {};
    for (const [field, messages] of Object.entries(error.fields)) {
      mapped[field] = messages[0];
    }
    setErrors(mapped);
  }, []);

  return {
    state,
    set,
    patch,
    setStone,
    addStone,
    removeStone,
    reset,
    errors,
    dirty,
    check,
    applyServerErrors,
  };
}
